'use client';

import {
  BarChart3,
  ChevronRight,
  ClipboardList,
  Database,
  FileText,
  MoreHorizontal,
  Plus,
  Settings,
  Trash2,
  Users,
} from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { useUser } from '@/modules/common/application/context/user';
import { FeatureFlag, UserRole } from '@/modules/common/types';
import { AddProjectMemberDialog } from '@/modules/project/components/AddProjectMemberDialog';
import { CreateProjectDialog } from '@/modules/project/components/CreateProjectDialog';
import { DeleteProjectDialog } from '@/modules/project/components/DeleteProjectDialog';
import { ProjectSettingDialog } from '@/modules/project/components/ProjectSettingDialog';
import { useProjects } from '@/modules/project/hooks/use-projects';
import { ListProject } from '@/modules/project/types/Project';

export function NavProjects() {
  const { t } = useTranslation();
  const { projects, projectsLoaded, createProject, deleteProject, createLoading, deleteLoading } =
    useProjects({ fetchPolicy: 'network-only' });
  const { user } = useUser();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<ListProject | null>(null);
  const [projectToSettings, setProjectToSettings] = useState<ListProject | null>(null);
  const [projectToAddMember, setProjectToAddMember] = useState<ListProject | null>(null);
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const [openProjects, setOpenProjects] = useState<Set<string>>(new Set());
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());

  const isExternalUser = user?.role === UserRole.EXTERNAL;
  const isAnalyticsEnabled = useFeatureFlagEnabled(FeatureFlag.ANALYTICS) === true;

  const toggleProject = (projectId: string) => {
    const newOpenProjects = new Set(openProjects);
    if (newOpenProjects.has(projectId)) {
      newOpenProjects.delete(projectId);
    } else {
      newOpenProjects.add(projectId);
    }
    setOpenProjects(newOpenProjects);
  };

  const toggleDropdown = (projectId: string, open: boolean) => {
    const newOpenDropdowns = new Set(openDropdowns);
    if (open) {
      newOpenDropdowns.add(projectId);
    } else {
      newOpenDropdowns.delete(projectId);
    }
    setOpenDropdowns(newOpenDropdowns);
  };

  const closeDropdown = (projectId: string) => {
    const newOpenDropdowns = new Set(openDropdowns);
    newOpenDropdowns.delete(projectId);
    setOpenDropdowns(newOpenDropdowns);
  };

  const handleCreateProject = () => {
    setCreateDialogOpen(true);
  };

  const handleDeleteProject = (project: ListProject) => {
    closeDropdown(project.id);
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleSettingsProject = (project: ListProject) => {
    closeDropdown(project.id);
    setProjectToSettings(project);
    setSettingsDialogOpen(true);
  };

  const handleAddMembers = (project: ListProject) => {
    closeDropdown(project.id);
    setProjectToAddMember(project);
    setAddMemberDialogOpen(true);
  };

  const isProjectOwner = (project: ListProject): boolean => {
    if (!user?.id || !project.members) return false;
    return project.members.some((member) => member.userId === user.id && member.role === 'OWNER');
  };

  if (!projectsLoaded) {
    return (
      <>
        <SidebarGroup className="group-data-[collapsible=icon]:hidden">
          <SidebarGroupLabel>{t('modules.mavue2.navigation.projects')}</SidebarGroupLabel>
          {!isExternalUser && (
            <SidebarGroupAction
              title={t('modules.mavue2.navigation.createProject')}
              onClick={handleCreateProject}
            >
              <Plus />
              <span className="sr-only">{t('modules.mavue2.navigation.createProject')}</span>
            </SidebarGroupAction>
          )}
          <SidebarMenu>
            <SidebarMenuItem className="px-2 py-1">
              <Skeleton className="h-6 w-full bg-muted-foreground/10" />
            </SidebarMenuItem>
            <SidebarMenuItem className="px-2 py-1">
              <Skeleton className="h-6 w-full bg-muted-foreground/10" />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        <CreateProjectDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onCreate={(input) => createProject({ variables: { input } })}
          createLoading={createLoading}
        />
      </>
    );
  }

  if (projects.length === 0 && !isExternalUser) {
    return (
      <>
        <SidebarGroup className="group-data-[collapsible=icon]:hidden">
          <SidebarGroupLabel>{t('modules.mavue2.navigation.projects')}</SidebarGroupLabel>
          <SidebarGroupAction
            title={t('modules.mavue2.navigation.createProject')}
            onClick={handleCreateProject}
          >
            <Plus />
            <span className="sr-only">{t('modules.mavue2.navigation.createProject')}</span>
          </SidebarGroupAction>
          <SidebarMenu>
            <SidebarMenuItem>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={handleCreateProject}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('modules.mavue2.navigation.createNewProject')}
              </Button>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        <CreateProjectDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onCreate={(input) => createProject({ variables: { input } })}
          createLoading={createLoading}
        />
      </>
    );
  }

  return (
    <>
      <SidebarGroup className="group-data-[collapsible=icon]:hidden">
        <SidebarGroupLabel>{t('modules.mavue2.navigation.projects')}</SidebarGroupLabel>
        {!isExternalUser && (
          <SidebarGroupAction
            title={t('modules.mavue2.navigation.createProject')}
            onClick={handleCreateProject}
          >
            <Plus />
            <span className="sr-only">{t('modules.mavue2.navigation.createProject')}</span>
          </SidebarGroupAction>
        )}
        <SidebarMenu>
          {projects.map((project) => {
            const isProjectOpen = openProjects.has(project.id);
            const isDropdownOpen = openDropdowns.has(project.id);
            const isProjectActive = location.pathname.startsWith(`/project/${project.id}`);
            const isReportsActive = location.pathname.startsWith(
              `/project/${project.id}/reporting`
            );
            const isAnalyticsActive =
              isAnalyticsEnabled &&
              location.pathname.startsWith(`/project/${project.id}/analytics`);
            const isDocumentsActive = location.pathname.startsWith(
              `/project/${project.id}/documents`
            );
            const isCustomDataActive = location.pathname.startsWith(
              `/project/${project.id}/custom-data`
            );
            const isOwner = isProjectOwner(project);

            return (
              <Collapsible
                key={project.id}
                asChild
                open={isProjectOpen}
                onOpenChange={() => toggleProject(project.id)}
              >
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip={project.title}
                    isActive={
                      isProjectActive &&
                      !isReportsActive &&
                      !isAnalyticsActive &&
                      !isDocumentsActive &&
                      !isCustomDataActive
                    }
                  >
                    <div className="flex items-center w-full">
                      <CollapsibleTrigger className="flex items-center flex-1 min-w-0">
                        <ChevronRight
                          className={`mr-1 h-4 w-4 transition-transform ${
                            isProjectOpen ? 'rotate-90' : ''
                          }`}
                        />
                        <span className="truncate cursor-pointer">{project.title}</span>
                      </CollapsibleTrigger>
                    </div>
                  </SidebarMenuButton>
                  {!isExternalUser && (
                    <DropdownMenu
                      open={isDropdownOpen}
                      onOpenChange={(open) => toggleDropdown(project.id, open)}
                    >
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuAction showOnHover>
                          <MoreHorizontal />
                          <span className="sr-only">{t('modules.mavue2.navigation.more')}</span>
                        </SidebarMenuAction>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        className="w-48 rounded-lg"
                        side={isMobile ? 'bottom' : 'right'}
                        align={isMobile ? 'end' : 'start'}
                      >
                        {isOwner && (
                          <DropdownMenuItem onClick={() => handleAddMembers(project)}>
                            <Users className="text-muted-foreground" />
                            <span>{t('modules.mavue2.navigation.addMembers')}</span>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleSettingsProject(project)}>
                          <Settings className="text-muted-foreground" />
                          <span>{t('modules.mavue2.navigation.projectSettings')}</span>
                        </DropdownMenuItem>
                        {isOwner && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeleteProject(project)}>
                              <Trash2 className="text-muted-foreground" />
                              <span>{t('modules.mavue2.navigation.deleteProject')}</span>
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={isReportsActive}
                          onClick={() => navigate(`/project/${project.id}/reporting`)}
                        >
                          <div className="flex items-center cursor-pointer">
                            <ClipboardList className="mr-2 h-4 w-4" />
                            <span>{t('modules.mavue2.navigation.reports')}</span>
                          </div>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      {!isExternalUser && isAnalyticsEnabled && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isAnalyticsActive}
                            onClick={() => navigate(`/project/${project.id}/analytics`)}
                          >
                            <div className="flex items-center cursor-pointer">
                              <BarChart3 className="mr-2 h-4 w-4" />
                              <span>{t('modules.mavue2.navigation.analytics')}</span>
                            </div>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {!isExternalUser && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isCustomDataActive}
                            onClick={() => navigate(`/project/${project.id}/custom-data`)}
                          >
                            <div className="flex items-center cursor-pointer">
                              <Database className="mr-2 h-4 w-4" />
                              <span>{t('modules.mavue2.navigation.datapoints')}</span>
                            </div>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={isDocumentsActive}
                          onClick={() => navigate(`/project/${project.id}/documents`)}
                        >
                          <div className="flex items-center cursor-pointer">
                            <FileText className="mr-2 h-4 w-4" />
                            <span>{t('modules.mavue2.navigation.documents')}</span>
                          </div>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            );
          })}
        </SidebarMenu>
      </SidebarGroup>

      <CreateProjectDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreate={(input) => createProject({ variables: { input } })}
        createLoading={createLoading}
      />
      {projectToAddMember && (
        <AddProjectMemberDialog
          projectId={projectToAddMember.id}
          open={addMemberDialogOpen}
          onOpenChange={(open) => {
            setAddMemberDialogOpen(open);
            if (!open) {
              setProjectToAddMember(null);
            }
          }}
        />
      )}

      {projectToSettings && (
        <ProjectSettingDialog
          projectId={projectToSettings.id}
          open={settingsDialogOpen}
          onOpenChange={(open) => {
            setSettingsDialogOpen(open);
            if (!open) {
              setProjectToSettings(null);
            }
          }}
        />
      )}

      {projectToDelete && (
        <DeleteProjectDialog
          project={projectToDelete}
          open={deleteDialogOpen}
          deleteLoading={deleteLoading}
          onOpenChange={(open) => {
            setDeleteDialogOpen(open);
            if (!open) {
              setProjectToDelete(null);
            }
          }}
          onDelete={(id) => deleteProject({ variables: { id } })}
        />
      )}
    </>
  );
}
